/**
 * ComplianceUpdatesPanel.tsx — Compliance Auto-Updater admin panel (Layer 6).
 *
 * Shows:
 *  - Current vs latest Prowler version
 *  - Update history table (date / source / type / status)
 *  - Pending Review items highlighted in amber
 *  - "Approve" button per pending item
 *  - "Check Now" manual trigger
 */

import React, { useEffect, useState } from 'react';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import axios from 'axios';
import API_BASE from '../config/api';

// ── Types ──────────────────────────────────────────────────────────────────────

interface UpdateLogEntry {
  id: string;
  source: string;
  update_type: string;
  previous_version: string | null;
  new_version: string | null;
  changes_summary: string;
  affected_frameworks: string[];
  affected_controls_count: number;
  status: 'applied' | 'pending_review' | 'failed';
  applied_at: string | null;
  reviewed_by: string | null;
  created_at: string;
}

interface ProwlerVersion {
  current_version: string;
  latest_version: string | null;
  update_available: boolean;
  error?: string;
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function StatusChip({ status }: { status: string }) {
  const map: Record<string, { label: string; color: 'success' | 'warning' | 'error' | 'default' }> = {
    applied:        { label: 'Applied',         color: 'success' },
    pending_review: { label: 'Pending Review',  color: 'warning' },
    failed:         { label: 'Failed',          color: 'error'   },
  };
  const cfg = map[status] ?? { label: status, color: 'default' };
  return <Chip label={cfg.label} color={cfg.color} size="small" />;
}

// ── Main component ──────────────────────────────────────────────────────────────

interface Props {
  /** Only show if compact=true; defaults to full panel */
  compact?: boolean;
}

export default function ComplianceUpdatesPanel({ compact = false }: Props) {
  const [logs, setLogs]             = useState<UpdateLogEntry[]>([]);
  const [prowler, setProwler]       = useState<ProwlerVersion | null>(null);
  const [loading, setLoading]       = useState(true);
  const [checking, setChecking]     = useState(false);
  const [approving, setApproving]   = useState<string | null>(null);
  const [msg, setMsg]               = useState('');
  const [msgSeverity, setMsgSeverity] = useState<'success' | 'error' | 'info'>('info');
  const [lastCheck, setLastCheck]   = useState<Date | null>(null);

  async function fetchAll() {
    setLoading(true);
    try {
      const [logsRes, pvRes] = await Promise.all([
        axios.get(`${API_BASE}/v1/compliance/updates`),
        axios.get(`${API_BASE}/v1/compliance/updates/prowler-version`),
      ]);
      setLogs(logsRes.data ?? []);
      setProwler(pvRes.data);
      setLastCheck(new Date());
    } catch {
      flash('Failed to load compliance update data.', 'error');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { fetchAll(); }, []);

  function flash(text: string, severity: 'success' | 'error' | 'info' = 'info') {
    setMsg(text);
    setMsgSeverity(severity);
  }

  async function handleCheckNow() {
    setChecking(true);
    setMsg('');
    try {
      const res = await axios.post(`${API_BASE}/v1/compliance/updates/check-now`);
      flash(res.data.message ?? 'Check complete.', 'success');
      await fetchAll();
    } catch {
      flash('Check failed — see server logs.', 'error');
    } finally {
      setChecking(false);
    }
  }

  async function handleApprove(id: string) {
    setApproving(id);
    try {
      const res = await axios.post(`${API_BASE}/v1/compliance/updates/${id}/approve`);
      flash(res.data.message ?? 'Approved.', 'success');
      setLogs((prev) =>
        prev.map((l) => l.id === id ? { ...l, status: 'applied' } : l)
      );
    } catch {
      flash('Failed to approve update.', 'error');
    } finally {
      setApproving(null);
    }
  }

  const pendingCount = logs.filter((l) => l.status === 'pending_review').length;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box>
      {/* ── Header row ─────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CloudSyncIcon color="primary" fontSize="small" />
          <Typography variant="h6" fontWeight={700}>Compliance Auto-Updater</Typography>
          {pendingCount > 0 && (
            <Chip
              label={`${pendingCount} pending`}
              color="warning"
              size="small"
              icon={<WarningAmberIcon fontSize="small" />}
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {lastCheck && (
            <Typography variant="caption" color="text.secondary">
              Last check: {lastCheck.toLocaleTimeString()}
            </Typography>
          )}
          <Button
            variant="outlined"
            size="small"
            startIcon={checking ? <CircularProgress size={14} /> : <RefreshIcon />}
            onClick={handleCheckNow}
            disabled={checking}
          >
            {checking ? 'Checking…' : 'Check Now'}
          </Button>
        </Box>
      </Box>

      {msg && (
        <Alert severity={msgSeverity} sx={{ mb: 2 }} onClose={() => setMsg('')}>
          {msg}
        </Alert>
      )}

      {/* ── Prowler version card ───────────────────────────────────────── */}
      {prowler && (
        <Paper
          sx={{
            p: 2,
            mb: 2.5,
            borderRadius: 2,
            border: prowler.update_available
              ? '1px solid rgba(247,127,0,0.4)'
              : '1px solid rgba(46,204,113,0.3)',
            bgcolor: prowler.update_available
              ? 'rgba(247,127,0,0.05)'
              : 'rgba(46,204,113,0.04)',
          }}
        >
          <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              {prowler.update_available
                ? <WarningAmberIcon sx={{ color: '#f77f00' }} fontSize="small" />
                : <CheckCircleIcon  sx={{ color: '#2ecc71' }} fontSize="small" />}
              <Typography variant="body2" fontWeight={600}>Prowler</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Installed: <code>{prowler.current_version}</code>
            </Typography>
            {prowler.latest_version && (
              <Typography variant="body2" color="text.secondary">
                Latest: <code>{prowler.latest_version}</code>
              </Typography>
            )}
            {prowler.update_available && (
              <Chip label="Update Available" color="warning" size="small" />
            )}
            {!prowler.update_available && prowler.latest_version && (
              <Chip label="Up to date" color="success" size="small" />
            )}
            {prowler.error && (
              <Typography variant="caption" color="error.main">
                (GitHub unreachable — {prowler.error.slice(0, 60)})
              </Typography>
            )}
          </Stack>
        </Paper>
      )}

      {/* ── Update log table ───────────────────────────────────────────── */}
      {logs.length === 0 ? (
        <Box
          sx={{
            textAlign: 'center',
            py: 5,
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 2,
          }}
        >
          <HourglassEmptyIcon sx={{ color: 'text.disabled', fontSize: 36, mb: 1 }} />
          <Typography color="text.secondary" variant="body2">
            No update log entries yet. Click "Check Now" to run the first check.
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Frameworks</TableCell>
                <TableCell>Summary</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(compact ? logs.slice(0, 8) : logs).map((entry) => (
                <TableRow
                  key={entry.id}
                  sx={{
                    bgcolor: entry.status === 'pending_review'
                      ? 'rgba(247,127,0,0.06)'
                      : entry.status === 'failed'
                      ? 'rgba(239,35,60,0.04)'
                      : undefined,
                  }}
                >
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(entry.created_at).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="caption"
                      sx={{ fontFamily: 'monospace', textTransform: 'uppercase' }}
                    >
                      {entry.source}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">{entry.update_type}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {entry.affected_frameworks?.join(', ') || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 320 }}>
                    <Tooltip title={entry.changes_summary}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        noWrap
                        sx={{ display: 'block', maxWidth: 300 }}
                      >
                        {entry.changes_summary}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <StatusChip status={entry.status} />
                  </TableCell>
                  <TableCell align="right">
                    {entry.status === 'pending_review' && (
                      <Button
                        size="small"
                        variant="contained"
                        color="warning"
                        disabled={approving === entry.id}
                        onClick={() => handleApprove(entry.id)}
                        sx={{ minWidth: 80 }}
                      >
                        {approving === entry.id ? <CircularProgress size={14} /> : 'Approve'}
                      </Button>
                    )}
                    {entry.reviewed_by && (
                      <Typography variant="caption" color="text.disabled" display="block">
                        by {entry.reviewed_by}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
